import React from 'react';
import { useNavigationWithReturn } from '../../hooks/useNavigationWithReturn';
import { ExternalLink } from 'lucide-react';

/**
 * Props pour le composant ResponsiveAmount
 */
interface ResponsiveAmountProps {
  value: number;
  currency?: string;
  showCurrency?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  colorize?: boolean; // Colorer en fonction du signe
}

/**
 * Formate un montant de manière responsive
 * Adapte automatiquement l'affichage selon la taille
 */
export function formatResponsiveAmount(value: number, compact: boolean = false): string {
  const absValue = Math.abs(value);
  
  if (compact) {
    if (absValue >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(1)}Md`;
    }
    if (absValue >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
      return `${(value / 1_000).toFixed(0)}K`;
    }
  }
  
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Composant pour afficher un montant de manière responsive
 */
export function ResponsiveAmount({
  value,
  currency = 'CDF',
  showCurrency = true,
  className = '',
  size = 'md',
  colorize = false
}: ResponsiveAmountProps) {
  const sizeClasses: Record<string, string> = {
    'xs': 'text-xs',
    'sm': 'text-sm',
    'md': 'text-base',
    'lg': 'text-lg',
    'xl': 'text-xl',
    '2xl': 'text-2xl'
  };

  const colorClass = colorize
    ? value >= 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400'
    : '';

  return (
    <span className={`font-medium tabular-nums ${sizeClasses[size]} ${colorClass} ${className}`}>
      {/* Version complète pour écrans larges */}
      <span className="hidden sm:inline">
        {formatResponsiveAmount(value, false)}
      </span>
      {/* Version compacte pour mobile */}
      <span className="sm:hidden">
        {formatResponsiveAmount(value, true)}
      </span>
      {showCurrency && (
        <span className="ml-1 text-muted-foreground font-normal text-[0.85em]">
          {currency}
        </span>
      )}
    </span>
  );
}

/**
 * Props pour le composant AccountCodeLink
 */
interface AccountCodeLinkProps {
  code: string;
  accountId?: string;
  label?: string;
  showIcon?: boolean;
  className?: string;
  variant?: 'badge' | 'text' | 'subtle';
}

/**
 * Composant pour afficher un code de compte cliquable
 * Navigue vers le grand livre avec le compte sélectionné
 */
export function AccountCodeLink({
  code,
  accountId,
  label,
  showIcon = false,
  className = '',
  variant = 'badge'
}: AccountCodeLinkProps) {
  const { navigateToLedger } = useNavigationWithReturn();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateToLedger(code, accountId);
  };

  if (variant === 'text') {
    return (
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center gap-1 
          text-primary hover:text-primary/80 
          hover:underline cursor-pointer
          transition-colors
          ${className}
        `}
        title={`Voir le compte ${code} dans le grand livre`}
      >
        <span className="font-mono">{code}</span>
        {label && <span>- {label}</span>}
        {showIcon && <ExternalLink className="h-3 w-3 opacity-50" />}
      </button>
    );
  }

  if (variant === 'subtle') {
    return (
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center gap-1
          text-xs text-muted-foreground
          hover:text-primary cursor-pointer
          transition-colors
          ${className}
        `}
        title={`Voir le compte ${code} dans le grand livre`}
      >
        <span className="font-mono">{code}</span>
        {showIcon && <ExternalLink className="h-2.5 w-2.5 opacity-50" />}
      </button>
    );
  }

  // Variant 'badge' (default)
  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center gap-1
        font-mono text-xs 
        px-1.5 py-0.5 
        bg-muted hover:bg-primary/10 
        text-muted-foreground hover:text-primary
        rounded border border-transparent hover:border-primary/20
        cursor-pointer transition-all
        ${className}
      `}
      title={`Voir le compte ${code} dans le grand livre`}
    >
      {code}
      {showIcon && <ExternalLink className="h-2.5 w-2.5 opacity-50" />}
    </button>
  );
}

/**
 * Props pour le composant AccountItem
 */
interface AccountItemProps {
  code: string;
  name: string;
  balance: number;
  accountId?: string;
  currency?: string;
  showDebitCredit?: boolean;
  debit?: number;
  credit?: number;
  className?: string;
}

/**
 * Composant pour afficher une ligne de compte avec code cliquable
 */
export function AccountItem({
  code,
  name,
  balance,
  accountId,
  currency = 'CDF',
  showDebitCredit = false,
  debit = 0,
  credit = 0,
  className = ''
}: AccountItemProps) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${className}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <AccountCodeLink code={code} accountId={accountId} variant="badge" />
        <span className="truncate text-sm">{name}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {showDebitCredit && (
          <>
            <ResponsiveAmount 
              value={debit} 
              currency={currency}
              showCurrency={false}
              size="sm"
              className="text-blue-600 dark:text-blue-400 w-20 text-right"
            />
            <ResponsiveAmount 
              value={credit} 
              currency={currency}
              showCurrency={false}
              size="sm"
              className="text-purple-600 dark:text-purple-400 w-20 text-right"
            />
          </>
        )}
        <ResponsiveAmount 
          value={balance} 
          currency={currency}
          size="sm"
          colorize
          className="w-24 text-right"
        />
      </div>
    </div>
  );
}
